import type Phaser from "phaser";

import { resetBuildingTextureCleanupCache } from "./building-texture-cleanup.js";
import { groundTextureCache } from "./map-ground-bake.js";

const CASE_TEXTURE_PREFIXES = [
  "npc_art_",
  "bld_art_",
  "ground_diag_",
  "interior_ground_",
] as const;
const CASE_TEXTURE_KEYS = new Set(["placeholder_npc", "placeholder_bld"]);

/** True for per-case art loaded into the shared Phaser texture manager. */
export function isCaseScopedTextureKey(key: string): boolean {
  if (CASE_TEXTURE_KEYS.has(key)) return true;
  return CASE_TEXTURE_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/** Drop case art from a reused Phaser game instance before the next scene boot. */
export function purgeCaseTextures(game: Phaser.Game): void {
  resetBuildingTextureCleanupCache();
  groundTextureCache.clear();
  for (const key of game.textures.getTextureKeys()) {
    if (isCaseScopedTextureKey(key)) {
      game.textures.remove(key);
    }
  }
  const gameScene = game.scene.getScene("GameScene") as Phaser.Scene | undefined;
  try {
    gameScene?.load?.reset();
  } catch {
    /* loader may be mid-flight during scene transition */
  }
}

/** Regenerate a procedural placeholder without "Texture key already in use". */
export function ensureGeneratedTexture(
  scene: Phaser.Scene,
  key: string,
  width: number,
  height: number,
  paint: (g: Phaser.GameObjects.Graphics) => void
): void {
  if (scene.textures.exists(key)) {
    scene.textures.remove(key);
  }
  const g = scene.add.graphics();
  paint(g);
  g.generateTexture(key, width, height);
  g.destroy();
}
